import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getF2TokenNew } from '@moal/api'
import { useContext, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { useWordpressPageStatus } from '../../../hooks/useWordpressPageStatus'
import Modal from '../../Utilities/Modal'
import WordpressPage from '../../Utilities/WordpressPage'

// const SCANNER_URL = process.env.REACT_APP_F2_URL
const CUSTOMER = process.env.REACT_APP_F2_CUSTOMER

const DEFAULT_TIER = '1'

const F2Scanner = ({ product }) => {
  const scannerDetails = product.scanner
  const [searchParams] = useSearchParams()
  const debug = searchParams.get('debug') === 'true'
  const { user } = useContext(AuthenticationContext)
  const [currentTier, setCurrentTier] = useState(DEFAULT_TIER)
  const [maxTier, setMaxTier] = useState(DEFAULT_TIER)
  const [token, setToken] = useState('')
  const [iframeUrl, setIframeURL] = useState('')

  useEffect(() => {
    const getToken = async () => {
      // Grab the user's ID and subscriptions
      const userId = user?.userService?._id
      const subs = user?.userService?.subscriptions

      const subType = subs?.find(
        (sub) => sub.itemNumber === product.itemNumber
      )?.subType

      const tier =
        scannerDetails.tiers.find((t) => t.subType === subType)?.tier ||
        DEFAULT_TIER

      setCurrentTier(tier)

      const maxTier =
        scannerDetails.tiers.length > 0
          ? Math.max(
              ...scannerDetails.tiers.map((item) => parseInt(item.tier))
            ).toString()
          : DEFAULT_TIER

      setMaxTier(maxTier)

      if (userId && tier) {
        const payload = {
          user_identifier: userId,
          customer: CUSTOMER,
          access_tier: tier,
          scanner: scannerDetails.name,
          scanners: [scannerDetails.name]
        }

        const response = await getF2TokenNew({
          token: localStorage.getItem('authToken'),
          data: payload
        })
        if (response.status === 200 && response.data !== null) {
          setToken(response.data.token)
          setIframeURL(response.data.url)
        }
      }
    }
    if (user && iframeUrl.length === 0) {
      getToken()
    }
  }, [user, iframeUrl])

  return (
    <div className="flex h-full w-full flex-col">
      {debug && (
        <div className="rounded-lg bg-grayscale-100 p-4 shadow">
          <h2 className="text-lg font-semibold text-grayscale-800">Debug</h2>
          <p className="text-grayscale-600">
            Token:{' '}
            <span className="break-words rounded bg-grayscale-200 px-2 font-mono text-sm">
              {token}
            </span>
          </p>
          <p className="text-grayscale-600">
            URL:{' '}
            <span className="break-words rounded bg-grayscale-200 px-2 font-mono text-sm">
              {iframeUrl}
            </span>
          </p>
        </div>
      )}
      <UpgradeNotice
        currentTier={currentTier}
        maxTier={maxTier}
        upgrade={scannerDetails.upgrade}
      />
      <Warning product={product} />
      {token.length > 0 ? (
        <ContentContainer product={product} iframeUrl={iframeUrl} />
      ) : (
        <h2>Loading...</h2>
      )}
      <Disclaimer product={product} />
    </div>
  )
}

export default F2Scanner

const ContentContainer = ({ product, iframeUrl }) => {
  const [open, setOpen] = useState(false)
  const slug = `${product.scanner.name}-scanner-guide`
  const { content } = useWordpressPageStatus(product, slug)

  return (
    <div className="flex h-full w-full flex-col gap-1">
      {content && (
        <>
          <button
            className="button-fill m-2 w-fit text-sm"
            onClick={() => setOpen(true)}
          >
            Scanner Guide
          </button>
          <Modal
            open={open}
            setOpen={setOpen}
            size="md"
            cancelText="Close"
            title="Scanner Guide"
          >
            <WordpressPage content={content} />
          </Modal>
        </>
      )}
      <iframe className="h-full w-full" src={iframeUrl}></iframe>
    </div>
  )
}

const UpgradeNotice = ({ currentTier, maxTier, upgrade }) => {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) {
    return null
  }

  if (parseInt(currentTier) < parseInt(maxTier)) {
    return (
      <div className="relative border border-slate-700 bg-slate-900 py-3 pl-3 pr-6">
        <p className="text-xs text-amber-300 sm:inline">{upgrade.copy}</p>
        <Link
          to={upgrade.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 rounded-md bg-amber-300 bg-gradient-to-r from-amber-300 to-amber-500 px-3 py-1 text-base font-bold uppercase text-black hover:from-amber-400 hover:to-amber-600 active:from-amber-400 active:to-amber-600"
        >
          Upgrade
        </Link>
        <button
          className="absolute right-0 top-0 p-2 text-amber-300 hover:text-amber-100 active:text-amber-100"
          onClick={() => setDismissed(true)}
        >
          <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
        </button>
      </div>
    )
  }
  return null
}

const Warning = ({ product }) => {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) {
    return null
  }

  return (
    <div className="relative border border-amber-700 bg-amber-900 py-3 pl-3 pr-6 md:hidden">
      <p className="text-xs text-amber-300 sm:inline">
        For the best experience, view {product.displayName} on a device with a
        wider screen.
      </p>
      <button
        className="absolute right-0 top-0 p-2 text-amber-300 hover:text-amber-100 active:text-amber-100"
        onClick={() => setDismissed(true)}
      >
        <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
      </button>
    </div>
  )
}

const Disclaimer = ({ product }) => {
  return (
    <div className="relative border border-slate-700 bg-slate-900 py-3 pl-3 pr-6">
      <p className="text-xs text-amber-300 sm:inline">
        The data presented by {product.displayName} should not be considered
        securities advice, and you should always do your own due diligence
        before investing.
      </p>
    </div>
  )
}
